import * as React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import LinearProgress from "@mui/material/LinearProgress";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";
import { ResponsiveChartContainer } from "@mui/x-charts/ResponsiveChartContainer";
import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot, MarkPlot } from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsGrid } from "@mui/x-charts/ChartsGrid";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { useTranslation } from "react-i18next";
import {
  DASHBOARD_GRAPH_PERIODS,
  DEFAULT_DASHBOARD_GRAPH_PERIOD,
  useDailyActivityUsersGraphQuery,
} from "@query/dashboardGraphQuery";
import {
  formatDashboardDate,
  formatDashboardDateRange,
  resolveDashboardLocale,
} from "../utils/chartLocale";

const PERIOD_TRANSLATION_KEYS = {
  "1year": "main:dashboard.periods.oneYear",
  "6months": "main:dashboard.periods.sixMonths",
  "3months": "main:dashboard.periods.threeMonths",
  "1month": "main:dashboard.periods.oneMonth",
  "1week": "main:dashboard.periods.oneWeek",
};

function getTickStep(length) {
  if (length > 180) return 30;
  if (length > 90) return 14;
  if (length > 45) return 7;
  if (length > 21) return 3;
  return 1;
}

function formatUsers(value, formatter, t) {
  return `${formatter.format(value ?? 0)} ${t("main:dashboard.dailyUsers.usersShort")}`;
}

export default function ActivityDailyUsersChart() {
  const theme = useTheme();
  const { t, i18n } = useTranslation(["main"]);
  const [period, setPeriod] = React.useState(DEFAULT_DASHBOARD_GRAPH_PERIOD);
  const chartHeight = 320;

  const dailyUsersGraphQuery = useDailyActivityUsersGraphQuery(period);
  const payload = dailyUsersGraphQuery.data?.data;
  const locale = resolveDashboardLocale(i18n.language);

  const numberFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        maximumFractionDigits: 1,
      }),
    [locale]
  );

  const chartPoints = React.useMemo(() => {
    if (!Array.isArray(payload?.daily)) {
      return [];
    }

    return payload.daily.map((point) => ({
      rawDate: point.date,
      dateLabel: formatDashboardDate(point.date, locale, {
        month: "short",
        day: "numeric",
      }),
      usersCount: Number(point.users_count) || 0,
    }));
  }, [payload?.daily, locale]);

  const totalUsers = React.useMemo(
    () => chartPoints.reduce((sum, point) => sum + point.usersCount, 0),
    [chartPoints]
  );

  const averageUsers = chartPoints.length > 0 ? totalUsers / chartPoints.length : 0;

  const peakPoint = React.useMemo(() => {
    if (chartPoints.length === 0) {
      return null;
    }

    return chartPoints.reduce(
      (peak, point) => (point.usersCount > (peak?.usersCount || -1) ? point : peak),
      null
    );
  }, [chartPoints]);

  const peakLabel =
    peakPoint && peakPoint.usersCount > 0
      ? `${formatDashboardDate(peakPoint.rawDate, locale, {
          month: "short",
          day: "numeric",
        })} (${formatUsers(
          peakPoint.usersCount,
          numberFormatter,
          t
        )})`
      : t("main:dashboard.dailyUsers.noPeak");

  const rangeLabel = React.useMemo(
    () =>
      formatDashboardDateRange(
        payload?.from_date,
        payload?.to_date,
        locale,
        t("main:dashboard.periodRangeFallback")
      ),
    [payload?.from_date, payload?.to_date, locale, t]
  );

  const xAxisData = chartPoints.map((point) => point.dateLabel);
  const seriesData = chartPoints.map((point) => point.usersCount);
  const hasData = chartPoints.length > 0;
  const isLoading = dailyUsersGraphQuery.isLoading && !payload;

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        borderRadius: 3,
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background:
          "linear-gradient(180deg, rgba(3, 169, 244, 0.12) 0%, rgba(3, 169, 244, 0) 38%)",
      }}
    >
      {dailyUsersGraphQuery.isFetching && !isLoading ? (
        <LinearProgress />
      ) : (
        <Box sx={{ height: 4 }} />
      )}
      <CardContent sx={{ pt: 2.5, display: "flex", flexDirection: "column", flex: 1 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{
            alignItems: { xs: "stretch", sm: "flex-start" },
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
              <PeopleAltRoundedIcon sx={{ color: theme.palette.info.main }} fontSize="small" />
              <Typography component="h2" variant="subtitle2">
                {t("main:dashboard.dailyUsers.title")}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "center", flexWrap: "wrap", mb: 0.5 }}
            >
              <Typography variant="h6" component="p" sx={{ fontWeight: 700 }}>
                {formatUsers(averageUsers, numberFormatter, t)}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                color="info"
                label={t("main:dashboard.dailyUsers.averagePerDay")}
              />
            </Stack>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
              {rangeLabel}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {peakLabel}
            </Typography>
          </Box>

          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 170 } }}>
            <InputLabel id="daily-users-period-label">
              {t("main:dashboard.period.label")}
            </InputLabel>
            <Select
              labelId="daily-users-period-label"
              id="daily-users-period"
              value={period}
              label={t("main:dashboard.period.label")}
              onChange={(event) => setPeriod(event.target.value)}
            >
              {DASHBOARD_GRAPH_PERIODS.map((periodValue) => (
                <MenuItem key={periodValue} value={periodValue}>
                  {t(PERIOD_TRANSLATION_KEYS[periodValue])}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
            mb: 2,
            px: 1.25,
            py: 1,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <InfoOutlinedIcon sx={{ color: "text.secondary", fontSize: 18, mt: "2px" }} />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("main:dashboard.dailyUsers.description")}
          </Typography>
        </Box>

        <Box sx={{ mt: "auto" }}>
          {isLoading ? <Skeleton variant="rounded" height={chartHeight} /> : null}

          {!isLoading && dailyUsersGraphQuery.isError ? (
            <Alert severity="error">{t("main:dashboard.errors.loadDailyUsers")}</Alert>
          ) : null}

          {!isLoading && !dailyUsersGraphQuery.isError && !hasData ? (
            <Box
              sx={{
                height: chartHeight,
                borderRadius: 2,
                border: "1px dashed",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("main:dashboard.noData")}
              </Typography>
            </Box>
          ) : null}

          {!isLoading && !dailyUsersGraphQuery.isError && hasData ? (
            <Box sx={{ height: chartHeight }}>
              <ResponsiveChartContainer
                series={[
                  {
                    type: "bar",
                    id: "daily-users-bars",
                    label: t("main:dashboard.dailyUsers.columnsLabel"),
                    data: seriesData,
                    color: theme.palette.info.light,
                    valueFormatter: (value) => formatUsers(value, numberFormatter, t),
                  },
                  {
                    type: "line",
                    id: "daily-users-line",
                    label: t("main:dashboard.dailyUsers.trendLabel"),
                    data: seriesData,
                    color: theme.palette.info.main,
                    curve: "monotoneX",
                    showMark: seriesData.length <= 45,
                    valueFormatter: (value) => formatUsers(value, numberFormatter, t),
                  },
                ]}
                xAxis={[
                  {
                    scaleType: "band",
                    data: xAxisData,
                    tickInterval: (_, index) => index % getTickStep(xAxisData.length) === 0,
                  },
                ]}
                yAxis={[
                  {
                    valueFormatter: (value) => formatUsers(value, numberFormatter, t),
                  },
                ]}
                margin={{ left: 60, right: 20, top: 20, bottom: 36 }}
                height={chartHeight}
                sx={{
                  "& .MuiBarElement-series-daily-users-bars": {
                    opacity: 0.72,
                  },
                  "& .MuiLineElement-series-daily-users-line": {
                    strokeWidth: 3,
                    filter: "drop-shadow(0px 6px 10px rgba(2, 136, 209, 0.24))",
                  },
                  "& .MuiMarkElement-series-daily-users-line": {
                    fill: theme.palette.info.main,
                    stroke: theme.palette.info.main,
                    strokeWidth: 1,
                  },
                }}
              >
                <ChartsGrid horizontal />
                <BarPlot borderRadius={4} />
                <LinePlot />
                <MarkPlot />
                <ChartsXAxis />
                <ChartsYAxis />
                <ChartsTooltip />
              </ResponsiveChartContainer>
            </Box>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
}
